<template>
  <div v-if="tab" class="w-screen h-screen pb-safe overflow-hidden flex flex-col">
    <top-bar :show-back="true" @back="router.back()">
      <template #center>
        <div v-if="tab" class="flex items-center font-heading font-bold text-xl">
          <span v-if="tab.code">
            <span class="font-bold">{{ tab.code }}</span>
            <span v-if="tab.tableName"> - {{ tab.tableName }}</span>
            <span v-else-if="tab.name"> - {{ tab.name }}</span>
          </span>
          <span v-else-if="tab.tableName">{{ tab.tableName }}</span>
          <span v-else>{{ tab.name }}</span>

          <l-dropdown
            v-model:open="openTabOptions"
            :options="tabOptions"
            :mark-selected="false"
            placement="bottom"
          >
            <l-button
              v-if="tab.open"
              size="small"
              type="text"
              icon="bracket-down"
              @click="openTabOptions = true"
            />
          </l-dropdown>
        </div>
      </template>
    </top-bar>

    <div class="flex w-full h-full overflow-hidden gap-4 z-1">
      <div class="flex flex-col max-w-80 shrink w-full border-n-600 border-r">
        <checkout-tab
          :selected-product-ids="selectedProductsIds"
          class="flex-1"
          :tab-id="tabId"
          @toggle-product="toggleProduct"
        />
        <terrace-surcharge
          v-if="tableHasSurcharge"
          :tab-id="tabId"
          @toggle="onTerraceSurchargeToggled"
        />
        <balance class="mb-4" :tab-id="tabId" />
      </div>
      <div class="flex justify-center w-full overflow-y-auto">
        <div class="w-full h-full max-w-96 py-7 flex flex-col">
          <div class="flex items-center justify-between pb-4 font-heading text-n-0 w-full">
            <div class="font-medium">
              {{ t('checkout.total') }}
            </div>
            <div v-if="selectedBill" class="text-xl font-bold">
              {{ currency(selectedBill.total) }}
            </div>
          </div>
          <reservation-deposit-warning :reservation="reservation" />
          <charge-calculator
            :payment-methods="paymentMethods"
            :initial-pending="(selectedBill && selectedBill.pending) || 0"
            :show-probable-amounts="false"
            keypad-mode="full"
            display-mode="pending"
            :autofill="false"
            @change-payment-method="selectPaymentMethod"
            @calculator-change="calculatorChange"
          />
          <div class="mt-auto">
            <l-button class="w-full" :disabled="isCharging || !canCharge" @click="() => charge()">
              {{ t('checkout.charge') }}
              {{ currency(calculatorValues.toPay) }}
            </l-button>
          </div>
          <div class="grid grid-cols-2 gap-2 mt-2">
            <bill-discount
              v-model:discount="discount"
              :disabled="isExisting(selectedBillId)"
              :original-total="selectedBill && selectedBill.originalTotal"
              :tab-id="tabId"
            />
            <l-button v-if="selectedBill" @click="printBill">
              {{ t('checkout.print') }}
            </l-button>
          </div>
        </div>
      </div>
      <div class="flex max-w-80 w-full">
        <bills-list
          class="p-6"
          :current-bill-id="currentBill && currentBill.id"
          :bills="bills"
          :selected-bill-id="selectedBillId"
          :tab-id="tabId"
          @bill-selected="id => (selectedBillId = id)"
          @invalidate="invalidateAndRegenerate"
          @cancel-invoice="cancelInvoice"
          @generate-invoice="id => (generateInvoiceBillId = id)"
          @send-invoice="id => (sendInvoiceBillId = id)"
        />
      </div>
    </div>

    <generate-invoice
      v-if="generateInvoiceBillId"
      :tab-id="tabId"
      :bill-id="generateInvoiceBillId"
      @new-bill-id="id => (selectedBillId = id)"
      @close="generateInvoiceBillId = ''"
    />
    <send-invoice
      v-if="sendInvoiceBillId"
      :bill-id="sendInvoiceBillId"
      @close="sendInvoiceBillId = ''"
    />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { LButton, LDropdown, useDialog } from '@last/core-ui/paprika'
import type { Option } from '@last/core-ui/paprika/components/dropdown/types'

import Balance from '@/components/Balance.vue'
import type { CalculatorPayload } from '@/components/Calculator/types'
import ChargeCalculator from '@/components/ChargeCalculator/ChargeCalculator.vue'
import TopBar from '@/components/TopBar.vue'
import { useCheckout } from '@/composables/useCheckout'
import { useMoney } from '@/composables/useMoney'
import { useTabs } from '@/composables/useTabs'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import BillDiscount from '@/modules/checkout/BillDiscount.vue'
import BillsList from '@/modules/checkout/BillsList.vue'
import ReservationDepositWarning from '@/modules/checkout/ReservationDepositWarning.vue'
import TerraceSurcharge from '@/modules/checkout/TerraceSurcharge.vue'
import { useFloorplanStore } from '@/store/floorplan'
import { usePromotionsStore } from '@/store/promotions'
import { useReservationsStore } from '@/store/reservations'
import { useTabsStore } from '@/store/tabs'
import type { Reservation, TabPromotion } from '@/types'

import GenerateInvoice from '../invoices/GenerateInvoice.vue'
import SendInvoice from '../invoices/SendInvoice.vue'
import CheckoutTab from './CheckoutTab.vue'

const route = useRoute()
const router = useRouter()

const tabId = route.params.tabId as string

const floorplanStore = useFloorplanStore()
const tabsStore = useTabsStore()
const promotionsStore = usePromotionsStore()
const dialog = useDialog()
const { findReservationByTabId } = useReservationsStore()

const reservation = ref<Reservation | undefined>(undefined)

const { tables } = storeToRefs(floorplanStore)

const { addBill, removeBill, updateTerraceSurcharge, regenerateBill } = tabsStore

const { tab, allBills, allProducts, tabGlobalPromotion, closeTab } = useTabs(() => tabId)

const openTabOptions = ref(false)
const calculatorValues = ref<CalculatorPayload>({
  toPay: 0,
  pending: 0,
  change: 0,
  tip: 0
})

const {
  selectedBillId,
  currentBill,
  selectedProductsIds,
  paymentMethods,
  isCharging,
  bills,
  canCharge,
  selectedBill,
  checkoutCompleteWithPayments,
  charge,
  selectPaymentMethod
} = useCheckout(tabId, 'normalCheckout', calculatorValues)

const { currency } = useMoney()
const { t } = useI18n()
const currentDiscount = ref<TabPromotion | null>(null)
const generateInvoiceBillId = ref<string>('')
const sendInvoiceBillId = ref<string>('')

const tabOptions: Option[] = [
  {
    label: t('ordering.close'),
    value: 'close',
    onClick: () => closeTab()
  }
]

onMounted(() => {
  reservation.value = findReservationByTabId(tabId)
  if (bills.value && bills.value.length > 0) {
    selectedBillId.value = bills.value[0].id
  }
  if (tabGlobalPromotion.value) {
    currentDiscount.value = {
      type: tabGlobalPromotion.value.discountType,
      amount: tabGlobalPromotion.value.discountAmount,
      promotionId: tabGlobalPromotion.value.promotionId,
      concept: tabGlobalPromotion.value.concept
    }
  }
})

const discount = computed({
  get() {
    if (isExisting(selectedBillId.value)) {
      return selectedBill.value.discount || {}
    }
    return currentDiscount.value
  },
  set(discount) {
    if (currentDiscount.value?.promotionId && !discount.promotionId) {
      promotionsStore.deleteTabPromotion({
        tabId: tabId,
        tabPromotion: tabGlobalPromotion.value
      })
      currentDiscount.value = null
    }
    promotionsStore.updateTabPromotion({
      tabId: tabId,
      promotion: {
        discountType: discount.type,
        discountAmount: discount.amount,
        name: discount.name,
        id: discount.promotionId,
        global: discount.global
      }
    })
    currentDiscount.value = discount
  }
})

const tableHasSurcharge = computed(() => {
  if (!tab || !tab.value) return false
  const table = tables.value[tab.value.tables[0]]

  return (
    (table && table.terraceSurchargePercentage > 0) ||
    (table?.flatFeeSurcharge && table?.flatFeeSurcharge > 0) ||
    (tab.value.terraceSurchargePercentage && tab.value.terraceSurchargePercentage > 0) ||
    (tab.value.flatFeeSurcharge && tab.value.flatFeeSurcharge > 0)
  )
})

function calculatorChange(value: CalculatorPayload) {
  calculatorValues.value = value
}

watch(checkoutCompleteWithPayments, complete => {
  const tabProducts = allProducts.value
  if (complete && tabProducts.length > 0) {
    closeTab()
  }
})

function printBill() {
  addBill({ tabId: tabId, bill: selectedBill.value })
  TicketPrinter.printBill(selectedBill.value)
  selectedProductsIds.value = []
  currentDiscount.value = {}
}

function invalidateAndRegenerate(billId: string) {
  dialog({
    title: t('checkout.invalidate-bill'),
    content: t('checkout.invalidate-bill-content'),
    mainLabel: t('ctas.yes'),
    secondaryLabel: t('ctas.cancel'),
    onConfirm: () => {
      removeBill({ tabId: tabId, billId })
      if (currentBill.value) {
        selectedBillId.value = currentBill.value.id
      } else if (bills.value && bills.value.length > 0) {
        selectedBillId.value = bills.value[0].id
      }
    }
  })
}

function isExisting(billId: string): boolean {
  return allBills.value.map(bill => bill.id).includes(billId)
}

function toggleProduct(productId: { id: string; uniqueId: string }): void {
  if (selectedProductsIds.value.map(id => id.uniqueId).includes(productId.uniqueId)) {
    selectedProductsIds.value = selectedProductsIds.value.filter(
      id => id.uniqueId != productId.uniqueId
    )
  } else {
    selectedProductsIds.value = [...selectedProductsIds.value, productId]
  }
  selectedBillId.value = currentBill.value.id
}

async function cancelInvoice(billId: string) {
  selectedBillId.value = regenerateBill({
    tabId: tabId,
    billId: billId,
    customerCompany: undefined
  })
}

function onTerraceSurchargeToggled(enabled: boolean) {
  updateTerraceSurcharge({
    tabId,
    enabled
  })
}
</script>
